import React, { useState, useEffect, useCallback } from "react"
import {
    Box,
    Button,
    Container,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    Paper,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    TextField,
    Typography,
    IconButton,
    FormControl,
    InputLabel,
    Select,
    MenuItem,
    FormControlLabel,
    Switch,
} from "@mui/material"
import EditIcon from "@mui/icons-material/Edit"
import AddIcon from "@mui/icons-material/Add"
import { useParams } from "react-router-dom"
import { useTranslation } from "react-i18next"
import * as adminService from "../../services/adminService"

const PaymentMethodsAdminPage: React.FC = () => {
    const { t } = useTranslation()
    const { companyId } = useParams<{ companyId: string }>()
    const [paymentMethods, setPaymentMethods] = useState<any[]>([])
    const [openDialog, setOpenDialog] = useState(false)
    const [editingMethod, setEditingMethod] = useState<any>(null)

    const [formData, setFormData] = useState({
        code: "",
        label: "",
        type: "cash",
        is_active: true,
    })

    const loadPaymentMethods = useCallback(async () => {
        if (!companyId) return
        try {
            const data = await adminService.getPaymentMethods(companyId)
            setPaymentMethods(data)
        } catch (error) {
            console.error("Failed to load payment methods:", error)
        }
    }, [companyId])

    useEffect(() => {
        loadPaymentMethods()
    }, [loadPaymentMethods])

    const handleOpenDialog = (method?: any) => {
        if (method) {
            setEditingMethod(method)
            setFormData({
                code: method.code,
                label: method.label,
                type: method.type,
                is_active: method.is_active,
            })
        } else {
            setEditingMethod(null)
            setFormData({
                code: "",
                label: "",
                type: "cash",
                is_active: true,
            })
        }
        setOpenDialog(true)
    }

    const handleCloseDialog = () => {
        setOpenDialog(false)
        setEditingMethod(null)
    }

    const handleSave = async () => {
        if (!companyId) return
        try {
            if (editingMethod) {
                await adminService.updatePaymentMethod(
                    companyId,
                    editingMethod.id,
                    formData
                )
            } else {
                await adminService.createPaymentMethod(companyId, formData)
            }
            handleCloseDialog()
            loadPaymentMethods()
        } catch (error) {
            console.error("Failed to save payment method:", error)
        }
    }

    return (
        <Box component="main" sx={{ flexGrow: 1, p: 3 }}>
            <Container maxWidth="lg">
                <Typography variant="h4" gutterBottom>
                    {t("settings.paymentMethods.title")}
                </Typography>

                <Box
                    sx={{ mb: 3, display: "flex", justifyContent: "flex-end" }}
                >
                    <Button
                        variant="contained"
                        startIcon={<AddIcon />}
                        onClick={() => handleOpenDialog()}
                    >
                        {t("settings.paymentMethods.add")}
                    </Button>
                </Box>

                <TableContainer component={Paper}>
                    <Table>
                        <TableHead>
                            <TableRow>
                                <TableCell>
                                    {t("settings.paymentMethods.fields.code")}
                                </TableCell>
                                <TableCell>
                                    {t("settings.paymentMethods.fields.label")}
                                </TableCell>
                                <TableCell>
                                    {t("settings.paymentMethods.fields.type")}
                                </TableCell>
                                <TableCell>
                                    {t(
                                        "settings.paymentMethods.columns.status"
                                    )}
                                </TableCell>
                                <TableCell>
                                    {t(
                                        "settings.paymentMethods.columns.actions"
                                    )}
                                </TableCell>
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {paymentMethods.map((method) => (
                                <TableRow key={method.id}>
                                    <TableCell>{method.code}</TableCell>
                                    <TableCell>{method.label}</TableCell>
                                    <TableCell>{method.type}</TableCell>
                                    <TableCell>
                                        {method.is_active
                                            ? t(
                                                  "settings.paymentMethods.active"
                                              )
                                            : t(
                                                  "settings.paymentMethods.inactive"
                                              )}
                                    </TableCell>
                                    <TableCell>
                                        <IconButton
                                            onClick={() =>
                                                handleOpenDialog(method)
                                            }
                                        >
                                            <EditIcon />
                                        </IconButton>
                                    </TableCell>
                                </TableRow>
                            ))}
                        </TableBody>
                    </Table>
                </TableContainer>

                <Dialog
                    open={openDialog}
                    onClose={handleCloseDialog}
                    maxWidth="sm"
                    fullWidth
                >
                    <DialogTitle>
                        {editingMethod
                            ? t("settings.paymentMethods.dialog.editTitle")
                            : t("settings.paymentMethods.dialog.title")}
                    </DialogTitle>
                    <DialogContent>
                        <Box
                            sx={{
                                display: "flex",
                                flexDirection: "column",
                                gap: 2,
                                mt: 1,
                            }}
                        >
                            <TextField
                                label={t("settings.paymentMethods.fields.code")}
                                required
                                fullWidth
                                value={formData.code}
                                onChange={(e) =>
                                    setFormData({
                                        ...formData,
                                        code: e.target.value,
                                    })
                                }
                                disabled={!!editingMethod}
                            />
                            <TextField
                                label={t(
                                    "settings.paymentMethods.fields.label"
                                )}
                                required
                                fullWidth
                                value={formData.label}
                                onChange={(e) =>
                                    setFormData({
                                        ...formData,
                                        label: e.target.value,
                                    })
                                }
                            />
                            <FormControl fullWidth required>
                                <InputLabel>
                                    {t("settings.paymentMethods.fields.type")}
                                </InputLabel>
                                <Select
                                    value={formData.type}
                                    label={t(
                                        "settings.paymentMethods.fields.type"
                                    )}
                                    onChange={(e) =>
                                        setFormData({
                                            ...formData,
                                            type: e.target.value,
                                        })
                                    }
                                    disabled={!!editingMethod}
                                >
                                    <MenuItem value="cash">
                                        {t(
                                            "settings.paymentMethods.types.cash"
                                        )}
                                    </MenuItem>
                                    <MenuItem value="card">
                                        {t(
                                            "settings.paymentMethods.types.card"
                                        )}
                                    </MenuItem>
                                    <MenuItem value="transfer">
                                        {t(
                                            "settings.paymentMethods.types.transfer"
                                        )}
                                    </MenuItem>
                                    <MenuItem value="check">
                                        {t(
                                            "settings.paymentMethods.types.check"
                                        )}
                                    </MenuItem>
                                    <MenuItem value="voucher">
                                        {t(
                                            "settings.paymentMethods.types.voucher"
                                        )}
                                    </MenuItem>
                                </Select>
                            </FormControl>
                            <FormControlLabel
                                control={
                                    <Switch
                                        checked={formData.is_active}
                                        onChange={(e) =>
                                            setFormData({
                                                ...formData,
                                                is_active: e.target.checked,
                                            })
                                        }
                                    />
                                }
                                label={t("settings.paymentMethods.active")}
                            />
                        </Box>
                    </DialogContent>
                    <DialogActions>
                        <Button onClick={handleCloseDialog}>
                            {t("common.cancel")}
                        </Button>
                        <Button onClick={handleSave} variant="contained">
                            {t("common.save")}
                        </Button>
                    </DialogActions>
                </Dialog>
            </Container>
        </Box>
    )
}

export default PaymentMethodsAdminPage
